import React, { useState } from "react";
import { Modal, Steps, Form, Select, Input, Button, message } from "antd";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";

const { Step } = Steps;
const { Option } = Select;

const AddConnectionModal = ({ visible, onClose, connectorId, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Step navigation
  const handleNext = async () => {
    try {
      await form.validateFields();
      if (currentStep === 0) {
        setCurrentStep(1);
      } else {
        handleSubmit();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Submit payload
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.getFieldsValue();
      console.log("Form values:", values);

      // normalize auth_type
      let authType = values.connectionType;
      if (["username_password", "client_credentials", "oauth2"].includes(authType)) {
        authType = "oauth2"; // backend expects just "oauth2"
      }

      const payload = {
        connector_config: {
          connector_id: connectorId,
          auth_type: authType,
          base_url: values.baseUrl || "",

          credentials: {
            api_key: values.apiKey,
            client_id: values.clientId,
            client_secret: values.clientSecret,
            username: values.username,
            password: values.password,
            token: values.jwtToken,
            grant_type:
              values.connectionType === "username_password"
                ? "password"
                : values.connectionType === "client_credentials"
                ? "client_credentials"
                : undefined,
          },

          auth_url: values.authUrl,
          token_url: values.tokenUrl,
        },
      };

      console.log("Submitting payload:", payload);

      const connectionService = new ApiService(
        `${BASE_URL}/connector_configs`,
        { "Content-Type": "application/json" },
        "POST",
        JSON.stringify(payload)
      );

      const response = await connectionService.makeCall();

      if (response?._id) {
        message.success("Connection added successfully");
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        message.error("Failed to add connection");
      }
    } catch (err) {
      console.error(err);
      message.error("Error while adding connection");
    } finally {
      setLoading(false);
    }
  };

  // Render dynamic fields
  const renderStepContent = () => {
    const type = form.getFieldValue("connectionType");

    if (currentStep === 0) {
      return (
        <Form.Item
          name="connectionType"
          label="Connection Type"
          rules={[{ required: true, message: "Please select a type" }]}
        >
          <Select placeholder="Select connection type">
            <Option value="oauth2">OAuth 2.0 (Auth Code)</Option>
            <Option value="client_credentials">OAuth 2.0 (Client Credentials)</Option>
            <Option value="username_password">OAuth 2.0 (Password Grant)</Option>
            <Option value="api_key">API Key</Option>
            <Option value="basic">Basic Auth</Option>
            <Option value="jwt">JWT</Option>
            <Option value="saml">SAML</Option>
          </Select>
        </Form.Item>
      );
    }

    if (currentStep === 1) {
      return (
        <>
          {/* Always show base_url */}
          <Form.Item
            name="baseUrl"
            label="Base URL"
            rules={[{ required: true, message: "Base URL is required" }]}
          >
            <Input />
          </Form.Item>

          {/* Switch based on type */}
          {type === "oauth2" && (
            <>
              <Form.Item name="clientId" label="Client ID" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="clientSecret" label="Client Secret" rules={[{ required: true }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item name="authUrl" label="Authorization URL" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="tokenUrl" label="Token URL" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}

          {type === "client_credentials" && (
            <>
              <Form.Item name="clientId" label="Client ID" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="clientSecret" label="Client Secret" rules={[{ required: true }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item name="tokenUrl" label="Token URL" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}

          {type === "username_password" && (
            <>
              <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item name="tokenUrl" label="Token URL" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}

          {type === "api_key" && (
            <Form.Item name="apiKey" label="API Key" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}

          {type === "basic" && (
            <>
              <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                <Input.Password />
              </Form.Item>
            </>
          )}

          {type === "jwt" && (
            <Form.Item name="jwtToken" label="JWT Token" rules={[{ required: true }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
          )}

          {type === "saml" && (
            <Form.Item name="samlMetadata" label="SAML Metadata XML" rules={[{ required: true }]}>
              <Input.TextArea rows={4} />
            </Form.Item>
          )}
        </>
      );
    }
  };

  return (
    <Modal
      title="Add New Connection"
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} size="small" style={{ marginBottom: 20 }}>
        <Step title="Select Type" />
        <Step title="Configure" />
      </Steps>

      <Form layout="vertical" form={form}>
        {renderStepContent()}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          {currentStep === 1 && (
            <Button style={{ marginRight: 8 }} onClick={() => setCurrentStep(0)}>
              Back
            </Button>
          )}
          <Button type="primary" loading={loading} onClick={handleNext}>
            {currentStep === 0 ? "Next" : "Submit"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddConnectionModal;
